import { useMutation as tanstackUseMutation, useQuery as tanstackUseQuery } from '@tanstack/react-query';
import { useAxiosInstance } from './ApiProvider.context';
import type {
  QueryFunctionContext,
  QueryKey,
  UseMutationOptions,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import type { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';

type AxiosQueryResult<TData> = {
  data: TData;
  response: AxiosResponse<TData>;
};

type AxiosQueryOptions<TQueryFnData, TError, TData, TQueryKey extends QueryKey> = Omit<
  UseQueryOptions<AxiosQueryResult<TQueryFnData>, TError, TData, TQueryKey>,
  'queryFn'
> & {
  request: AxiosRequestConfig<TQueryFnData>;
  axiosInstance?: AxiosInstance;
};

type AxiosMutationOptions<TData, TError, TVariables> = Omit<
  UseMutationOptions<TData, TError, TVariables>,
  'mutationFn'
> & {
  request: AxiosRequestConfig<TData>;
  axiosInstance?: AxiosInstance;
};

function createAxiosQueryFn<TQueryFnData, TQueryKey extends QueryKey>(
  request: AxiosRequestConfig<TQueryFnData>,
  axiosInstance: AxiosInstance,
) {
  return async ({ signal }: QueryFunctionContext<TQueryKey>) => {
    const response = await axiosInstance.request<TQueryFnData>({ ...request, signal });
    return { data: response.data, response };
  };
}

/**
 * Run a raw axios-backed query with a custom request and cache key.
 * Prefer the generated endpoint hooks from `@seeqdev/api/endpoint/...`
 * for consistent cache keys and SDK wiring.
 *
 * Note: Must be used within an `<ApiProvider>`
 */
export function useApiQuery<
  TQueryFnData = unknown,
  TError = unknown,
  TData = AxiosQueryResult<TQueryFnData>,
  TQueryKey extends QueryKey = QueryKey,
>(options: AxiosQueryOptions<TQueryFnData, TError, TData, TQueryKey>): UseQueryResult<TData, TError> {
  const { request, axiosInstance, ...rest } = options;
  const contextAxios = useAxiosInstance();
  const instance = axiosInstance ?? contextAxios;
  return tanstackUseQuery({
    ...rest,
    queryFn: createAxiosQueryFn<TQueryFnData, TQueryKey>(request, instance),
  });
}

/**
 * Run a raw axios-backed mutation with a custom request.
 * Prefer the generated endpoint hooks from `@seeqdev/api/endpoint/...`
 * for consistent cache keys and SDK wiring.
 *
 * Note: Must be used within an `<ApiProvider>`.
 */
export function useApiMutation<TData = unknown, TError = unknown, TVariables = unknown>(
  options: AxiosMutationOptions<TData, TError, TVariables>,
) {
  const { request, axiosInstance, ...rest } = options;
  const contextAxios = useAxiosInstance();
  const instance = axiosInstance ?? contextAxios;
  return tanstackUseMutation({
    ...rest,
    mutationFn: async (variables: TVariables) => {
      const response = await instance.request<TData>({
        ...request,
        data: variables,
      });
      return response.data;
    },
  });
}
